import React, { useEffect } from 'react'
import { $getSelection, $isRangeSelection, COMMAND_PRIORITY_EDITOR, createCommand, LexicalCommand } from 'lexical'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { useAppDispatch, useAppSelector } from '../../../../../../../../store/hooks'
import { selectMeetingNote, setMeetingNoteChangedExternally } from '../../../../../../../../store/meetingSummary/meetingSupport'
import { showToast } from '../../../../../../../../store/meetingSummary/display'
import { addHtmlContentToState, getEditorStateFromText, getHtmlContentFromState, parseIntoHeadlessEditor } from '../../utils'
import { useUpdateMeetingNote } from '../../../../hooks/api'
import { HTML_TYPE } from '../../../../../../../../common/copy'
import { isFirefox } from '../../../../utils'

export const ADD_TO_NOTEPAD_COMMAND: LexicalCommand<void> = createCommand('ADD_TO_NOTEPAD')

export const NotepadPlugin: React.FC = () => {
  const dispatch = useAppDispatch()

  const updateMeetingNote = useUpdateMeetingNote()

  const [editor] = useLexicalComposerContext()

  const meetingNote = useAppSelector(selectMeetingNote)

  useEffect(() => {
    const getHtmlContent = async () => {
      if (isFirefox()) {
        const text = await navigator.clipboard.readText()
        const editorState = getEditorStateFromText(text)
        return getHtmlContentFromState(JSON.stringify(editorState))
      }

      const clipboardContents = await navigator.clipboard.read()
      const htmlContentItem = clipboardContents.find((item) => item.types.includes(HTML_TYPE))
      if (!htmlContentItem) {
        return ''
      }
      const htmlBlob = await htmlContentItem.getType(HTML_TYPE)
      return await htmlBlob.text()
    }

    const copySelectionToNotepad = async () => {
      window.document.execCommand('copy')
      const htmlContent = await getHtmlContent()
      if (!htmlContent) {
        return
      }
      const headlessEditor = parseIntoHeadlessEditor(meetingNote)
      addHtmlContentToState(headlessEditor, htmlContent)
      const newMeetingNote = JSON.stringify(headlessEditor.getEditorState().toJSON())
      await updateMeetingNote(newMeetingNote)
      dispatch(setMeetingNoteChangedExternally(true))
    }
    return editor.registerCommand(
      ADD_TO_NOTEPAD_COMMAND,
      (_) => {
        const selection = $getSelection()

        if ($isRangeSelection(selection)) {
          copySelectionToNotepad().then(() => {
            const domSelection = window.getSelection()
            if (domSelection !== null) {
              domSelection.removeAllRanges()
            }
            navigator.clipboard.writeText('')
          })
          dispatch(showToast({ variant: 'success', icon: 'bi-check-circle-fill', message: 'Added to notepad!' }))
        }
        return true
      },
      COMMAND_PRIORITY_EDITOR,
    )
  }, [dispatch, editor, meetingNote, updateMeetingNote])

  return null
}
